import { Box, Popover } from '@mui/material';
import { CalendarIcon } from 'assets/common';
import { useState } from 'react';
import {
  useController,
  Control,
  Validate,
  FieldValues,
  FieldPath,
  RegisterOptions,
  PathValue,
} from 'react-hook-form';
import { useFlagState } from '../../../utilities/CustomHooks';
import { getFormattedDate } from '../../../utilities/DateUtils';
import { Input, Typography } from '../index';
import Calendar, { useRangeCalendar } from './Calendar';
import { isNullOrUndefined } from 'utilities/Utils';

type RangeCalendarProps = {
  popoverState: {
    isCalendarOpen: boolean;
    showCalendar: Function;
    hideCalendar: Function;
  };
  calendarProps: {
    from: Date;
    to: Date;
    enteredFrom: Date;
    enteredTo: Date;
    disabledBefore?: Date;
    disabledAfter?: Date;
    handleDayClick?: Function;
    handleDayMouseEnter?: Function;
    month?: Date;
    handleYearMonthChange?: Function;
    setRange?: Function;
    handleFromDayClick?: Function;
    handleToDayClick?: Function;
    setIsFirstClickInFromInput: Function;
    setIsFirstClickInToInput: Function;
  };

  showTodayButton?: boolean;
  inputDateFormat?: string;
  disabled?: boolean;
  error?: boolean;
  startDateText?: string;
  endDateText?: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  inputRef?: any;
  containerStyle?: React.CSSProperties;
  todayBtnText?: 'Today' | 'This Week';
  flexContainerProps?: any;
  isRangeCalendar?: boolean;
};
export enum INPUT_BOX_SELECTED_STATE {
  FROM_INPUT = 'FROM',
  TO_INPUT = 'TO',
}
const RangeCalendarTwoInputs = (props: RangeCalendarProps) => {
  const {
    popoverState,
    calendarProps,
    showTodayButton = false,
    todayBtnText = 'Today',
    inputDateFormat = 'DD MMM YYYY',
    disabled = false,
    error = false,
    startDateText = 'Start Date',
    endDateText = 'End Date',
    startPlaceholder = 'Start Date',
    endPlaceholder = 'End Date',
    inputRef = null,
    containerStyle,
    flexContainerProps = {},
    isRangeCalendar = true,
  } = props;
  const [inputBoxSelected, setInputBoxSelected] = useState<
  INPUT_BOX_SELECTED_STATE | undefined
  >(undefined);
  const [anchorEl, setAnchorEl] = useState(null);
  const { isCalendarOpen, showCalendar, hideCalendar } = popoverState;
  const { from, to, setIsFirstClickInFromInput, setIsFirstClickInToInput } =
    calendarProps;

  let inputFromVal = '',
    inputToVal = '';
  if (from) {
    const formattedFromVal = getFormattedDate(from, inputDateFormat);
    inputFromVal = formattedFromVal;
  }
  if (to) {
    const formattedToVal = getFormattedDate(to, inputDateFormat);
    inputToVal = formattedToVal;
  }
  const onFromInputClick = event => {
    setAnchorEl(event.currentTarget);
    showCalendar();
    setInputBoxSelected(INPUT_BOX_SELECTED_STATE.FROM_INPUT);
  };
  const onToInputClick = event => {
    setAnchorEl(event.currentTarget);
    showCalendar();
    setInputBoxSelected(INPUT_BOX_SELECTED_STATE.TO_INPUT);
  };
  const onPopoverClose = () => {
    hideCalendar();
    setIsFirstClickInFromInput(true);
    setIsFirstClickInToInput(true);
  };
  return (
    <>
      <Box display="flex" gap="16px" {...flexContainerProps}>
        <Box
          display="flex"
          flexDirection="column"
          gap="8px"
          style={containerStyle}
        >
          <Typography variant="body1" fb>
            {startDateText}
          </Typography>
          <Input
            value={inputFromVal}
            onClick={onFromInputClick}
            ref={inputRef}
            disabled={disabled}
            error={error}
            rightElement={<img src={CalendarIcon} alt="calendar-icon" />}
            placeholder={startPlaceholder}
            readOnly
            style={{ cursor: 'pointer' }} //classes we are not taking as a parameter thatswhy using style
          />
        </Box>
        {isRangeCalendar && (
          <Box
            display="flex"
            flexDirection="column"
            gap="8px"
            style={containerStyle}
          >
            <Typography variant="body1" fb>
              {endDateText}
            </Typography>
            <Input
              value={inputToVal}
              onClick={onToInputClick}
              ref={inputRef}
              disabled={disabled}
              error={error}
              rightElement={<img src={CalendarIcon} alt="calendar-icon" />}
              placeholder={endPlaceholder}
              readOnly
              style={{ cursor: 'pointer' }} //classes we are not taking as a parameter thatswhy using style
            />
          </Box>
        )}
      </Box>
      <Popover
        open={isCalendarOpen}
        anchorEl={anchorEl}
        onClose={onPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transitionDuration={'auto'}
        style={{ zIndex: 100000001 }}
      >
        <Calendar
          {...calendarProps}
          isRangeCalendar={isRangeCalendar}
          showTodayButton={showTodayButton}
          inputBoxSelected={inputBoxSelected}
          hideCalendar={popoverState.hideCalendar}
          todayBtnText={todayBtnText}
          strikedDates={[]} // Currently we don't a need of this, will add this as a props in future.
        />
      </Popover>
    </>
  );
};

export function useRangeCalendarTwoInputs(
  initialState,
  callback = () => {},
  disabledDays: any = {},
) {
  const [isCalendarOpen, showCalendar, hideCalendar] = useFlagState(false);
  const calendarProps = useRangeCalendar(initialState, callback, disabledDays);

  const popoverState = {
    isCalendarOpen,
    showCalendar,
    hideCalendar,
  };
  return {
    popoverState,
    calendarProps,
  };
}

type ControlledRangeCalendarInputProps<
  T extends FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> = Omit<
React.ComponentProps<typeof RangeCalendarTwoInputs>,
'popoverState' | 'calendarProps' | 'inputRef'
> & {
  name: TName; // this name is for useController
  control: Control<T, unknown>;
  defaultValue?: { from: Date; to: Date };
  isRequired?: boolean;
  customValidator?: Validate<PathValue<T, TName>, T>;
  disabledDays?: any;
};

export function useControlledRangeCalendarInput<
  T extends FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>(
  defaultValue: { from: Date; to: Date },
  name: TName,
  control: Control<T, unknown>,
  customValidator: Validate<PathValue<T, TName>, T>,
  isRequired: boolean,
  disabledDays?: any,
  isRangeCalendar?: boolean,
) {
  const initialState = isRangeCalendar
    ? [
      defaultValue?.from ? new Date(defaultValue?.from) : null,
      defaultValue?.to ? new Date(defaultValue?.to) : null,
    ]
    : [defaultValue?.from ? new Date(defaultValue?.from) : null];

  const validations: Partial<RegisterOptions<T, TName>> = {
    validate: (inputValue, formValues) => {
      if (customValidator) {
        return customValidator(inputValue, formValues);
      } else if (isRequired) {
        if (
          isNullOrUndefined(inputValue.from) ||
          (isRangeCalendar && isNullOrUndefined(inputValue.to))
        ) {
          return 'This field is required';
        }
      }
      return true;
    },
  };

  const {
    field: { onChange, ref, value },
  } = useController<T, TName>({
    name,
    rules: validations,
    control,
    defaultValue: (isRangeCalendar
      ? { from: initialState[0], to: initialState[1] }
      : { from: initialState[0] }) as PathValue<T, TName>,
  });

  const { popoverState, calendarProps } = useRangeCalendarTwoInputs(
    [value?.from && new Date(value?.from), value?.to && new Date(value?.to)],
    onChange,
    disabledDays,
  );

  return {
    popoverState,
    calendarProps,
    inputRef: ref,
    isRangeCalendar,
  };
}

export const ControlledRangeCalendarTwoInputs = <
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>(
    props: ControlledRangeCalendarInputProps<T, TName>,
  ) => {
  const {
    name,
    control,
    defaultValue = null,
    isRequired = false,
    customValidator,
    disabledDays = {},
    isRangeCalendar = true,
    ...additional
  } = props;
  const rangeCalendarInputProps = useControlledRangeCalendarInput(
    defaultValue,
    name,
    control,
    customValidator,
    isRequired,
    disabledDays,
    isRangeCalendar,
  );
  return (
    <RangeCalendarTwoInputs {...additional} {...rangeCalendarInputProps} />
  );
};

export default RangeCalendarTwoInputs;
